import React from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Paper,
  Typography,
  Alert,
  Collapse
} from '@mui/material';
import { Student } from '@/types';
import { PhotoUpload } from '@/components/PhotoUpload';
import { PhotoUploadError } from '@/components/PhotoUpload/types';
import { GENDER_OPTIONS, GRADE_LEVELS } from '../../../constants';

interface BasicInfoSectionProps {
  values: Partial<Student>;
  onChange: (id: keyof Student, value: any) => void;
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  values,
  onChange
}) => {
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const handleChange = (field: keyof Student) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange(field, event.target.value);
  };

  const handlePhotoError = (error: PhotoUploadError | null) => {
    setUploadError(error?.message || null);
  };

  return (
    <Grid container spacing={3}>
      {/* Profile Photo Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            <Box sx={{ width: 150 }}>
              <PhotoUpload
                value={values.photo}
                onChange={(file) => onChange('photo', file)}
                onError={handlePhotoError}
                previewSize={150}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Profile Photo
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload a professional photo of the student. The photo should be clear,
                recent, and show the student's face clearly.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported formats: JPG, PNG, WebP (max 5MB)
              </Typography>

              <Collapse in={!!uploadError} sx={{ width: '100%', mt: 2 }}>
                <Alert severity="error" onClose={() => setUploadError(null)}>
                  {uploadError}
                </Alert>
              </Collapse>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Basic Information Fields */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Name"
          value={values.firstName || ''}
          onChange={handleChange('firstName')}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name"
          value={values.lastName || ''}
          onChange={handleChange('lastName')}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Middle Name"
          value={values.middleName || ''}
          onChange={handleChange('middleName')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Preferred Name"
          value={values.preferredName || ''}
          onChange={handleChange('preferredName')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Date of Birth"
          value={values.dateOfBirth || ''}
          onChange={handleChange('dateOfBirth')}
          InputLabelProps={{ shrink: true }}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="Gender"
          value={values.gender || ''}
          onChange={handleChange('gender')}
        >
          {GENDER_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="Grade Level"
          value={values.grade || ''}
          onChange={handleChange('grade')}
          required
        >
          {GRADE_LEVELS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Enrollment Date"
          value={values.enrollmentDate || ''}
          onChange={handleChange('enrollmentDate')}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );
};